/**
 * Status badges.
 *
 * The colour of a status is defined ONCE here, so DRAFT looks identical on the
 * dashboard, in a list and on a detail page. A user learns the colour language
 * once instead of per screen — and it is never colour alone: every badge
 * carries its word, and a dot.
 */
import { Icon } from './Icon.jsx';

/** Workflow and record statuses, mapped to semantic variants. */
const STATUS_VARIANTS = {
  // Purchase requests
  DRAFT: 'default',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'default',

  // Purchase orders
  PENDING_APPROVAL: 'warning',
  SENT: 'info',
  PARTIALLY_RECEIVED: 'warning',
  RECEIVED: 'success',

  // Suppliers, users, organizations, memberships
  ACTIVE: 'success',
  // An organization created by a platform administrator whose owner has not
  // finished the profile yet. A working state, not a problem — hence info
  // rather than warning.
  PENDING_SETUP: 'info',
  INACTIVE: 'default',
  BLOCKED: 'danger',
  SUSPENDED: 'danger',

  // Goods receipts and modules
  POSTED: 'success',
  COMING_SOON: 'info',
};

const LABELS = {
  PENDING_SETUP: 'Pending setup',
  PENDING_APPROVAL: 'Pending approval',
  PARTIALLY_RECEIVED: 'Partly received',
  COMING_SOON: 'Coming soon',
};

function label(status) {
  if (LABELS[status]) return LABELS[status];
  const text = String(status ?? '').replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A status in words, exactly as its badge says it — for legends and charts. */
export const statusLabel = label;

/** The badge colour a status is shown in, for a legend or a bar beside it. */
export const statusVariant = (status) => STATUS_VARIANTS[status] ?? 'default';

/** `label` overrides the wording, for a screen that says it its own way. */
export function StatusBadge({ status, dot = true, label: text }) {
  if (!status) return <span className="subtle">—</span>;
  const variant = STATUS_VARIANTS[status] ?? 'default';
  const shown = text ?? label(status);

  return (
    <span className={`badge badge-${variant} ${dot ? 'badge-dot' : ''}`} title={shown}>
      {shown}
    </span>
  );
}

export function Badge({ variant = 'default', dot, children, title, size, style }) {
  return (
    <span className={`badge badge-${variant} ${dot ? 'badge-dot' : ''} ${size === 'sm' ? 'badge-sm' : ''}`} title={title} style={style}>
      {children}
    </span>
  );
}

/**
 * The workflow rail on a detail page: shows where a document sits in its
 * lifecycle, so a user can see what has happened and what comes next without
 * reading the audit trail.
 */
export function StatusSteps({ steps, current, terminal }) {
  const currentIndex = steps.indexOf(current);
  const isTerminal = terminal && !steps.includes(current);

  if (isTerminal) {
    return (
      <div className="steps">
        <span className="step step-current">{label(current)}</span>
      </div>
    );
  }

  return (
    <div className="steps" role="list" aria-label="Progress">
      {steps.map((step, index) => (
        <span key={step} className="row" style={{ gap: 4 }}>
          {index > 0 && (
            <span className="step-sep" aria-hidden="true">
              <Icon name="chevron-right" size={12} strokeWidth={2} />
            </span>
          )}
          <span
            role="listitem"
            className={`step ${index < currentIndex ? 'step-done' : ''} ${index === currentIndex ? 'step-current' : ''}`}
            aria-current={index === currentIndex ? 'step' : undefined}
          >
            {index < currentIndex && <Icon name="check" size={12} strokeWidth={2.4} />}
            {label(step)}
          </span>
        </span>
      ))}
    </div>
  );
}

export const PR_STEPS = ['DRAFT', 'SUBMITTED', 'APPROVED'];
export const PO_STEPS = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'SENT', 'RECEIVED'];
